import { Schema } from "effect";

/** Fixed, high-contrast palette; caller-supplied CSS is never accepted. */
export const kitchenTagColorSchema = Schema.Literal(
  "red",
  "amber",
  "green",
  "blue",
  "purple",
  "slate",
);
/** Kitchen preset colours, shared with the touch-friendly colour picker. */
export type KitchenTagColor = typeof kitchenTagColorSchema.Type;
/** Compare labels without case, outer whitespace or Unicode compatibility duplicates. */
export const kitchenLabelKey = (label: string) =>
  label.normalize("NFKC").trim().toLocaleLowerCase("en-AU");
const label = Schema.Trim.pipe(Schema.minLength(1), Schema.maxLength(40));
const tag = Schema.Struct({
  label,
  color: kitchenTagColorSchema,
  count: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThanOrEqualTo(1),
    Schema.lessThanOrEqualTo(99),
  ),
});
const uniqueLabels = (entries: readonly { readonly label: string }[]) =>
  new Set(entries.map((entry) => kitchenLabelKey(entry.label))).size ===
  entries.length;
/** Bounded item instructions, independent of provider notes and preset edits. */
export const kitchenNotesRecordSchema = Schema.Struct({
  tags: Schema.Array(tag).pipe(
    Schema.maxItems(12),
    Schema.filter(
      (entries) =>
        uniqueLabels(entries) ||
        "Each instruction can appear only once; increase its count instead.",
    ),
  ),
  text: Schema.Trim.pipe(Schema.maxLength(500)),
});
/** Validated wire/storage shape; use KitchenNotes for editing operations. */
export type KitchenNotesRecord = typeof kitchenNotesRecordSchema.Type;
/** Whole-order flags have no item counts and remain snapshots of the selected preset. */
export const orderQuickTagSchema = Schema.Struct({
  label,
  color: kitchenTagColorSchema,
});
/** Starter whole-order instructions, independent of counted item presets. */
export const defaultOrderQuickTags: readonly (typeof orderQuickTagSchema.Type)[] =
  [
    { label: "Takeaway", color: "blue" },
    { label: "Clean before preparing (vegan)", color: "purple" },
  ];
/** Retain legacy counted tags separately from new whole-order quick tags. */
export const orderKitchenNotesSchema = Schema.Struct({
  ...kitchenNotesRecordSchema.fields,
  text: Schema.Trim.pipe(Schema.maxLength(1500)),
  quick_tags: Schema.optional(
    Schema.Array(orderQuickTagSchema).pipe(
      Schema.maxItems(12),
      Schema.filter(
        (entries) =>
          uniqueLabels(entries) || "Order quick tags must be unique.",
      ),
    ),
  ),
});
/** Render earlier unassigned tags as text, preserving them until staff explicitly edits the information. */
export function orderInformation(
  notes: typeof orderKitchenNotesSchema.Type | undefined,
): string {
  if (!notes) return "";
  return [
    notes.text,
    notes.tags.length
      ? `Unassigned instructions from the previous version: ${notes.tags.map((tag) => `${tag.label} × ${tag.count}`).join(", ")}`
      : "",
  ]
    .filter(Boolean)
    .join("\n");
}
/** An editable merchant preset; order tags copy its wording and colour, not its identity. */
export const kitchenPresetSchema = Schema.Struct({
  id: Schema.UUID,
  label,
  color: kitchenTagColorSchema,
});
/** A parsed saved instruction preset. */
export type KitchenPreset = typeof kitchenPresetSchema.Type;
/** Optimistically versioned preset list. An explicit empty list remains empty. */
export const kitchenSettingsSchema = Schema.Struct({
  revision: Schema.Union(Schema.UUID, Schema.Literal("unversioned")),
  presets: Schema.Array(kitchenPresetSchema).pipe(
    Schema.maxItems(24),
    Schema.filter(
      (entries) => uniqueLabels(entries) || "Preset labels must be unique.",
    ),
    Schema.filter(
      (entries) =>
        new Set(entries.map((entry) => entry.id)).size === entries.length ||
        "Preset IDs must be unique.",
    ),
  ),
});
/** Merchant configuration returned by the settings boundary. */
export type KitchenSettings = typeof kitchenSettingsSchema.Type;
/** Full-list save requires the last observed revision to avoid lost settings edits. */
export const putKitchenSettingsSchema = Schema.Struct({
  expected_revision: kitchenSettingsSchema.fields.revision,
  presets: kitchenSettingsSchema.fields.presets,
});
/** Starter presets for a merchant without saved configuration; saving [] disables them. */
export const defaultKitchenSettings: KitchenSettings = {
  revision: "unversioned",
  presets: [
    {
      id: "db86c253-4b5a-4332-8fdc-6948d07679b1",
      label: "No egg",
      color: "red",
    },
    {
      id: "c17725d7-79de-4248-b6a7-208fa1542a77",
      label: "No sauce",
      color: "amber",
    },
    {
      id: "b86b37c3-d9ad-4dc9-9e44-fcc1c17bf799",
      label: "Sauce separately",
      color: "blue",
    },
  ],
};

/** Immutable editing value. Counts are bounded, decrementing one removes a tag. */
export class KitchenNotes {
  private readonly value: KitchenNotesRecord;
  private constructor(value: KitchenNotesRecord) {
    this.value = value;
  }
  /** Boundary parser constructs only bounded, unique instructions. */
  static readonly schema = Schema.transform(
    kitchenNotesRecordSchema,
    Schema.declare(
      (input): input is KitchenNotes => input instanceof KitchenNotes,
    ),
    {
      decode: (value) => new KitchenNotes(value),
      encode: (value) => value.toRecord(),
    },
  );
  /** Empty legacy orders need no database backfill. */
  static empty() {
    return new KitchenNotes({ tags: [], text: "" });
  }
  /** Immutable snapshot for rendering. */
  get tags(): readonly Readonly<KitchenNotesRecord["tags"][number]>[] {
    return this.value.tags;
  }
  /** Optional staff-entered clarification, never interpreted as HTML. */
  get text() {
    return this.value.text;
  }
  /** Whether anything should be displayed on the docket. */
  get present() {
    return this.tags.length > 0 || this.text.length > 0;
  }
  /** Copy for HTTP/persistence; callers cannot mutate the value through it. */
  toRecord(): KitchenNotesRecord {
    return { text: this.text, tags: this.tags.map((tag) => ({ ...tag })) };
  }
  /** Tap adds/increments a tag; an existing snapshot keeps its original colour. */
  increment(preset: Pick<KitchenPreset, "label" | "color">): KitchenNotes {
    const key = kitchenLabelKey(preset.label);
    const exists = this.tags.some((tag) => kitchenLabelKey(tag.label) === key);
    if (!exists && this.tags.length >= 12) return this;
    return new KitchenNotes({
      text: this.text,
      tags: exists
        ? this.tags.map((tag) =>
            kitchenLabelKey(tag.label) === key
              ? { ...tag, count: Math.min(99, tag.count + 1) }
              : { ...tag },
          )
        : [
            ...this.toRecord().tags,
            { label: preset.label, color: preset.color, count: 1 },
          ],
    });
  }
  /** Subtract one, with no zero/negative counts persisted. */
  decrement(label: string): KitchenNotes {
    return new KitchenNotes({
      text: this.text,
      tags: this.tags.flatMap((tag) =>
        kitchenLabelKey(tag.label) !== kitchenLabelKey(label)
          ? [{ ...tag }]
          : tag.count > 1
            ? [{ ...tag, count: tag.count - 1 }]
            : [],
      ),
    });
  }
  /** Remove an instruction entirely. */
  remove(label: string): KitchenNotes {
    return new KitchenNotes({
      text: this.text,
      tags: this.tags
        .filter((tag) => kitchenLabelKey(tag.label) !== kitchenLabelKey(label))
        .map((tag) => ({ ...tag })),
    });
  }
}
